import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Token storage helpers
const saveTokens = (access, refresh) => {
  if (access) localStorage.setItem('odelicieuse_access_token', access);
  if (refresh) localStorage.setItem('odelicieuse_refresh_token', refresh);
};

const getAccessToken = () => localStorage.getItem('odelicieuse_access_token');
const getRefreshToken = () => localStorage.getItem('odelicieuse_refresh_token');

const clearTokens = () => {
  localStorage.removeItem('odelicieuse_access_token');
  localStorage.removeItem('odelicieuse_refresh_token');
};

// Axios interceptor: attach Bearer token to every request
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  // Don't attach tokens to auth endpoints (login, register, refresh)
  const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/register') || url.includes('/api/auth/refresh');
  if (!isAuthEndpoint) {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // Don't use withCredentials - avoids CORS credential issues with proxies
  return config;
});

// Response interceptor: handle 401 with token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';

    // Never retry auth endpoints
    const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/register') || url.includes('/api/auth/refresh');

    // Public endpoints that don't require auth — retry without token if 401
    const isPublicEndpoint = url.includes('/api/products') || url.includes('/api/health');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // For public endpoints, retry without auth token instead of refreshing
      if (isPublicEndpoint && !originalRequest._retryPublic) {
        originalRequest._retryPublic = true;
        delete originalRequest.headers.Authorization;
        return axios(originalRequest);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          clearTokens();
          processQueue(new Error('No refresh token'));
          return Promise.reject(error);
        }
        const res = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken
        });

        if (res.data.access_token) {
          saveTokens(res.data.access_token, res.data.refresh_token);
        }
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Retour OAuth Google : AuthCallback établit la session d'abord
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Applique une réponse de connexion (ex. Google) : tokens + profil complet
  const applySession = useCallback(async (data) => {
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`);
      setUser(me.data);
      return me.data;
    } catch (_) {
      setUser({ id: data.id, phone: data.phone, name: data.name, role: data.role, pricing_tier: data.pricing_tier });
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (phone, password, remember = false) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { phone, password, remember },
      
    );
    if (response.data.access_token) {
      saveTokens(response.data.access_token, response.data.refresh_token);
    }
    // Fetch the full profile (status, pricing_tier, permissions, …) so the
    // app has the complete user shape immediately after login.
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`);
      setUser(me.data);
      return me.data;
    } catch (_) {
      const userData = {
        id: response.data.id,
        phone: response.data.phone,
        name: response.data.name,
        role: response.data.role,
        pricing_tier: response.data.pricing_tier,
      };
      setUser(userData);
      return userData;
    }
  }, []);

  const register = useCallback(async (phone, password, name, email = '') => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      { phone, password, name, email },
      
    );
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (_) {
      // Logout API failure is non-critical; clear local state regardless
    }
    clearTokens();
    setUser(false);
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'admin' || user?.role === 'employee';

  // Phase 38 — permissions: admins implicitly have all of them; employees
  // only have what the admin granted (stored as a list on their user doc).
  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role !== 'employee') return false;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  }, [user]);

  const value = useMemo(() => ({
    user, loading, isAuthenticated, isAdmin, isStaff,
    login, register, logout, checkAuth, refreshUser: checkAuth, hasPermission, applySession,
    // isAuthenticated/isAdmin/isStaff derive from user — no need in deps
  }), [user, loading, login, register, logout, checkAuth, hasPermission, applySession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
